import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

// material-ui
import { Popper } from '@mui/material';

import ComponentTypesIconWrapper from '../../../../components/componentTypesIcon/ComponentTypesIconWrapper';
// components
import AampeTooltip from '../../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../../constants/aampeTheme';
//icons
import ICONS from '../../../../constants/icons';

import useComponentTypesQuery from '../../../../hooks/component-types/useComponentTypesQuery';

import { ComponentTypeMapped } from '../../../../models/component-types-model';
// models
import { VariantElement } from '../../../../models/editor.model';

interface SlateVariantToolbarComponentProps {
    isHovered: boolean;
    element: VariantElement;
    onMouseChange: (isEntering: boolean) => void;
    onClick: () => void;
}

const SlateVariantToolbarComponent: FC<
    React.PropsWithChildren<SlateVariantToolbarComponentProps>
> = ({ isHovered, element, onMouseChange, onClick }) => {
    const { data: componentTypes } = useComponentTypesQuery();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const updateAnchorEl = useCallback(() => {
        const htmlElement = document.getElementById(element.id);
        setAnchorEl(htmlElement);
    }, [element]);

    useEffect(() => {
        if (isHovered) {
            updateAnchorEl();
        }
    }, [isHovered, updateAnchorEl]);

    const componentTypeData = useMemo(
        () => componentTypes?.map?.[element.variantType],
        [componentTypes, element],
    );

    return (
        <Popper
            open={!!(anchorEl && isHovered)}
            anchorEl={anchorEl}
            placement="top"
            onMouseEnter={() => onMouseChange(true)}
            onMouseLeave={() => onMouseChange(false)}
        >
            <Container>
                <VariantIconContainer>
                    <AampeTooltip
                        title={componentTypeData?.name}
                        placement={'top'}
                    >
                        <ComponentTypesIconWrapper
                            dataKey={
                                (componentTypeData as ComponentTypeMapped)
                                    ?.data_key
                            }
                        />
                    </AampeTooltip>
                </VariantIconContainer>
                <Divider />
                <AddButton onClick={onClick}>
                    <Icon size={15} />
                    <ButtonText>add alternates</ButtonText>
                </AddButton>
            </Container>
        </Popper>
    );
};

const Container = styled.div({
    padding: aampeTheme.spacing(0, 1),
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.white,
    boxShadow:
        '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
});

const AddButton = styled.div({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: aampeTheme.colors.gray600,
});

const VariantIconContainer = styled.span(() => ({
    backgroundColor: aampeTheme.colors.white,
    display: 'flex',
    alignSelf: 'center',
}));

const ButtonText = styled.span({
    lineHeight: '18px',
    fontSize: '12px',
});

const Icon = styled(ICONS.plus)(() => ({
    color: aampeTheme.colors.gray600,
}));

const Divider: any = styled.div({
    borderLeft: `1px solid ${aampeTheme.colors.bordersWG01}`,
    height: '100%',
});

export default SlateVariantToolbarComponent;
